import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Gauge, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DEMO_ROLES = [
  { key: "admin", label: "Admin", email: "renaldomontri@gmail.com", password: "Admin@123", testid: "demo-role-admin-button" },
  { key: "quality_manager", label: "Quality Manager", email: "quality.manager@calibralab.io", password: "Demo@123", testid: "demo-role-quality-button" },
  { key: "technical_manager", label: "Technical Manager", email: "technical.manager@calibralab.io", password: "Demo@123", testid: "demo-role-technical-button" },
  { key: "supervisor", label: "Supervisor", email: "supervisor@calibralab.io", password: "Demo@123", testid: "demo-role-supervisor-button" },
  { key: "technician", label: "Calibration Technician", email: "technician@calibralab.io", password: "Demo@123", testid: "demo-role-tech-button" },
  { key: "customer", label: "Customer", email: "customer@calibralab.io", password: "Demo@123", testid: "demo-role-customer-button" },
];

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const doLogin = async (e, p) => {
    setError("");
    setLoading(true);
    try {
      await login(e, p);
      navigate("/");
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = (role) => {
    setEmail(role.email);
    setPassword(role.password);
    doLogin(role.email, role.password);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B0F17] p-12 lg:flex">
        <img
          src="https://images.unsplash.com/photo-1581093577421-f561a654a353?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxsYWJvcmF0b3J5JTIwZXF1aXBtZW50JTIwY2FsaWJyYXRpb24lMjBtZXRyb2xvZ3klMjBzY2llbnRpc3R8ZW58MHx8fHwxNzg5NDA4OTc4fDA&ixlib=rb-4.1.0&q=85"
          alt="Calibration laboratory"
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/70 to-transparent" />
        <div className="relative flex items-center gap-2 text-white">
          <Gauge className="h-7 w-7 text-blue-500" />
          <span className="font-display text-xl font-bold tracking-tight">BMDLabsystem</span>
        </div>
        <div className="relative">
          <div className="mb-4 inline-flex items-center gap-2 rounded-sm border border-white/20 px-2.5 py-1 font-mono text-[11px] uppercase tracking-widest text-blue-300">
            <ShieldCheck className="h-3.5 w-3.5" /> ISO/IEC 17025 Compliant
          </div>
          <h1 className="font-display text-4xl font-bold leading-tight tracking-tight text-white xl:text-5xl">
            Precision you can
            <br />
            prove. Traceability
            <br />
            you can audit.
          </h1>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
            Laboratory Information Management System for calibration labs — customer records, quotations,
            equipment inward tracking with QR barcoding, and an immutable audit trail.
          </p>
        </div>
        <div className="relative font-mono text-[11px] uppercase tracking-widest text-slate-500">
          LIMS · Phase 1 · Metrology Operations
        </div>
      </div>

      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <Gauge className="h-6 w-6 text-primary" />
            <span className="font-display text-lg font-bold">BMDLabsystem</span>
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight">Sign in</h2>
          <p className="mt-1 text-sm text-muted-foreground">Access the laboratory management console.</p>

          <form
            className="mt-6 space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              doLogin(email, password);
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600 dark:text-red-400" data-testid="login-error-message">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>

          <div className="mt-8">
            <div className="mb-3 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Quick demo login</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  disabled={loading}
                  onClick={() => quickLogin(r)}
                  data-testid={r.testid}
                  className="rounded-md border border-border px-3 py-2 text-left text-xs font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-foreground disabled:opacity-50"
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
